import {datesEqual} from './date.js';
import {eventIntersects} from './events.js';
import {assign, uid} from './utils.js';

/**
 * A chunk is the part of an event that belongs to one cell of the grid. Every view builds chunks
 * its own way and adds its own fields to them, so the type below is the union of all of them.
 *
 * Note that `top` means different things depending on who filled it in, see below.
 *
 * @typedef {Object} EventChunk
 *
 * Filled in by createEventChunk(), for every view
 * @property {Object} event         the event the chunk belongs to
 * @property {Date} start           start of the chunk, not of the event
 * @property {Date} end             end of the chunk, not of the event
 * @property {boolean} zeroDuration whether the chunk starts and ends at the same time
 * @property {String} [id]          can be used as key in a Svelte keyed each block
 *
 * Placement in the grid, filled in when the chunk is created
 * @property {Number} [gridColumn]
 * @property {Number} [gridRow]
 * @property {Object} [resource]    the resource of the row, in views that have resources
 * @property {Array} [dates]        days the chunk spans, in dayGrid, the all-day slot and timeline
 *
 * dayGrid and the all-day slot, filled in by prepareAllDayChunks()
 * @property {Object} [long]        chunks passing through the cell, shared between them
 * @property {Object} [prev]        chunk placed in the same cell before this one
 *
 * timeGrid, filled in by its createChunks() and groupChunks(). The values are in seconds and are
 * turned into pixels when the event is rendered
 * @property {Number} [top]         offset from the start of the day
 * @property {Number} [height]
 * @property {Number} [maxHeight]
 * @property {Object} [group]       chunks overlapping in time, laid out side by side: {columns, end}
 * @property {Number} [groupColumn] index of the column within that group
 *
 * timeline, filled in by its createChunks() and prepareChunks(). `left` and `width` are in seconds
 * @property {Number} [left]        offset from the start of the first day of the chunk
 * @property {Number} [width]
 * @property {Number} [order]       position in the eventOrder order, for the strict layout
 * @property {Array} [cells]        cells the chunk spans, each with its chunks and those placed in the current pass
 * @property {Array} [rivals]       chunks sharing at least one column, competing for a place
 * @property {Array} [mates]        chunks of the same layoutGroup in the row, shared between them
 *
 * Result of the layout in dayGrid, the all-day slot and timeline, in pixels
 * @property {Number} [top]
 * @property {Number} [bottom]
 */

/**
 * @returns {EventChunk}
 */
export function createEventChunk(event, start, end) {
    start = event.start > start ? event.start : start;
    end = event.end < end ? event.end : end;
    return {
        start,
        end,
        event,
        zeroDuration: datesEqual(start, end)
    };
}

/**
 * Create event chunk for month view and all-day slot in week view
 */
export function createAllDayChunks(event, days, withId = true) {
    let dates = [];
    let lastEnd;
    let gridColumn;
    let gridRow;
    let resource;
    for (let {gridColumn: column, gridRow: row, resource: dayResource, dayStart, dayEnd, disabled} of days) {
        if (!disabled && eventIntersects(event, dayStart, dayEnd, dayResource)) {
            dates.push(dayStart);
            lastEnd = dayEnd;
            if (!gridColumn) {
                gridColumn = column;
                gridRow = row;
                resource = dayResource;
            }
        }
    }
    if (dates.length) {
        let chunk = createEventChunk(event, dates[0], lastEnd);
        // Chunk layout
        assign(chunk, {gridColumn, gridRow, resource, dates});
        if (withId) {
            assignChunkId(chunk);
        }

        return [chunk];
    }

    return [];
}

/**
 * Prepare event chunks for month view and all-day slot in week view
 */
export function prepareAllDayChunks(chunks) {
    let prevChunks = {};
    let longChunks = {};
    for (let chunk of chunks) {
        let {gridColumn, gridRow} = chunk;
        // Prepare long chunks
        for (let i = 1; i < chunk.dates.length; ++ i) {
            let key = `${gridRow}_${gridColumn + i}`;
            if (longChunks[key]) {
                longChunks[key].chunks.push(chunk);
            } else {
                longChunks[key] = {
                    sorted: false,
                    chunks: [chunk]
                };
            }
        }
        let key = `${gridRow}_${gridColumn}`;
        chunk.long = longChunks[key];
        // Connect with previous chunk
        chunk.prev = prevChunks[key];
        prevChunks[key] = chunk;
    }
}

export function repositionEvent(chunk, height, top = 1, gap = 1) {
    if (chunk.prev) {
        top = chunk.prev.bottom + gap;
    }
    let bottom = top + height;
    if (chunk.long) {
        let longChunks = chunk.long;
        if (!longChunks.sorted) {
            longChunks.chunks.sort((a, b) => a.top - b.top);
            longChunks.sorted = true;
        }
        for (let longChunk of longChunks.chunks) {
            if (top < longChunk.bottom && bottom > longChunk.top) {
                let offset = longChunk.bottom - top + gap;
                top += offset;
                bottom += offset;
            }
        }
    }
    assign(chunk, {top, bottom});

    return top;
}

export function assignChunkId(chunk) {
    let {event, gridColumn, gridRow} = chunk;
    chunk.id = `${uid(event)}-${gridColumn}-${gridRow}`;
}
